import React, { useEffect, useState } from "react";
import { CopilotModalShell } from "../../CopilotModalShell";

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="cua-review-field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error && <div className="cua-review-field-error" role="alert">{error}</div>}
    </div>
  );
}

function ListSection({ title, items, setItems, newItem, renderItem }) {
  return (
    <div className="cua-review-section">
      {title && <div className="cua-review-section-title">{title}</div>}
      {items.map((it, i) => (
        <div key={i} className="cua-review-row">
          {renderItem(it, (v) => {
            const next = items.slice();
            next[i] = v;
            setItems(next);
          })}
          <button
            type="button"
            className="cua-review-row-remove"
            aria-label="Remove"
            onClick={() => {
              const next = items.slice();
              next.splice(i, 1);
              setItems(next);
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="cua-review-row-add"
        onClick={() => setItems([...items, newItem()])}
      >
        + Add {title ? title.toLowerCase().replace(/s$/, "") : "item"}
      </button>
    </div>
  );
}

function ParameterSection({ params, setParams }) {
  return (
    <div className="cua-review-section">
      <div className="cua-review-section-title">Parameters</div>
      {params.map((p, i) => (
        <div key={i} className="cua-review-row cua-review-param">
          <input
            className="cua-review-param-name"
            placeholder="name"
            value={p.name || ""}
            onChange={(e) => {
              const next = params.slice();
              next[i] = { ...next[i], name: e.target.value };
              setParams(next);
            }}
          />
          <input
            className="cua-review-param-desc"
            placeholder="description"
            value={p.description || ""}
            onChange={(e) => {
              const next = params.slice();
              next[i] = { ...next[i], description: e.target.value };
              setParams(next);
            }}
          />
          <button
            type="button"
            className="cua-review-row-remove"
            aria-label="Remove parameter"
            onClick={() => {
              const next = params.slice();
              next.splice(i, 1);
              setParams(next);
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="cua-review-row-add"
        onClick={() => setParams([...params, { name: "", description: "" }])}
      >
        + Add parameter
      </button>
    </div>
  );
}

function StepSection({ steps, setSteps, params }) {
  const paramNames = (params || []).map((p) => p.name).filter(Boolean);
  const [dragIndex, setDragIndex] = useState(null);

  const moveStep = (from, to) => {
    if (from == null || from === to) return;
    const next = steps.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSteps(next);
  };

  return (
    <div className="cua-review-section">
      <div className="cua-review-section-title">Steps</div>
      {steps.map((s, i) => (
        <div
          key={i}
          className="cua-review-row cua-review-step"
          onDragOver={(e) => { if (dragIndex !== null) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); moveStep(dragIndex, i); setDragIndex(null); }}
        >
          <span
            className="cua-review-step-drag"
            draggable
            role="button"
            aria-label="Drag to reorder step"
            title="Drag to reorder"
            onDragStart={() => setDragIndex(i)}
            onDragEnd={() => setDragIndex(null)}
          >
            ⠿
          </span>
          <input
            className="cua-review-step-intent"
            placeholder="intent"
            value={s.intent || ""}
            onChange={(e) => {
              const next = steps.slice();
              next[i] = { ...next[i], intent: e.target.value };
              setSteps(next);
            }}
          />
          {paramNames.length > 0 && (
            <select
              className="cua-review-step-uses"
              value={s.uses_parameter || ""}
              onChange={(e) => {
                const next = steps.slice();
                next[i] = { ...next[i], uses_parameter: e.target.value || undefined };
                setSteps(next);
              }}
            >
              <option value="">(no parameter)</option>
              {paramNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="cua-review-row-remove"
            aria-label="Remove step"
            onClick={() => {
              const next = steps.slice();
              next.splice(i, 1);
              setSteps(next);
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="cua-review-row-add"
        onClick={() => setSteps([...steps, { intent: "" }])}
      >
        + Add step
      </button>
    </div>
  );
}

/**
 * Edit form for a workflow playbook — shared by the recording wizard (review a
 * fresh compile) and the library edit/clone flow. Driven by plain props so it
 * isn't tied to the recording controller:
 *   playbook     the playbook to edit
 *   error        {code, message} | null  (surfaces name_conflict inline)
 *   onSave       ({name, summary, playbookOverride}) => void   (required)
 *   saveLabel    submit button text
 *   onRecompile  optional → shows "↻ Re-compile" (recording only), with confirm
 *   onDiscard    optional → shows destructive "Discard" (recording only), with confirm
 *   onCancel     optional → shows a plain "Cancel" (edit/clone)
 */
export function ReviewForm({
  playbook,
  error,
  onSave,
  saveLabel = "Save workflow",
  onRecompile,
  onDiscard,
  onCancel,
}) {
  const pb = playbook || {};
  const [name, setName] = useState(pb.name || "");
  const [summary, setSummary] = useState(pb.summary || "");
  const [prereqs, setPrereqs] = useState(pb.prerequisites || []);
  const [params, setParams] = useState(pb.parameters || []);
  const [steps, setSteps] = useState(pb.steps || []);
  const [notes, setNotes] = useState(pb.notes || "");
  // null | "discard" | "recompile" — drives the in-app confirm sheet
  // (replaces window.confirm, which the panel context forbids).
  const [confirmAction, setConfirmAction] = useState(null);

  // Resync local state when the playbook prop changes (recompile, or loading a
  // different workflow to edit).
  useEffect(() => {
    const p = playbook || {};
    setName(p.name || "");
    setSummary(p.summary || "");
    setPrereqs(p.prerequisites || []);
    setParams(p.parameters || []);
    setSteps(p.steps || []);
    setNotes(p.notes || "");
  }, [playbook]);

  const nameError = error && error.code === "name_conflict"
    ? error.message || `You already have a workflow called "${name}".`
    : null;

  const handleSave = () => {
    const playbookOverride = {
      ...(playbook || {}),
      name,
      summary,
      prerequisites: prereqs,
      parameters: params,
      steps: steps.map((s, i) => ({ ...s, id: i + 1 })),
      notes,
    };
    onSave({ name, summary, playbookOverride });
  };

  const confirmCopy = {
    discard: {
      title: "Discard recording?",
      body: "Audio, frames, and the compiled playbook will be deleted. This can't be undone.",
      confirmLabel: "Discard",
      confirmClass: "copilot-modal-btn copilot-modal-btn--danger",
      run: onDiscard,
    },
    recompile: {
      title: "Re-compile workflow?",
      body: "Re-compile discards your edits to this form and runs the compile again.",
      confirmLabel: "Re-compile",
      confirmClass: "copilot-modal-btn copilot-modal-btn--primary",
      run: onRecompile,
    },
  };

  const confirm = confirmAction ? confirmCopy[confirmAction] : null;

  return (
    <>
    <form className="cua-review-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Field label="Name" htmlFor="cua-review-name" error={nameError}>
        <input
          id="cua-review-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Summary" htmlFor="cua-review-summary">
        <textarea
          id="cua-review-summary"
          rows={2}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
      </Field>

      <ListSection
        title="Prerequisites"
        items={prereqs}
        setItems={setPrereqs}
        newItem={() => ""}
        renderItem={(p, set) => (
          <input
            className="cua-review-prereq"
            value={p}
            onChange={(e) => set(e.target.value)}
          />
        )}
      />

      <ParameterSection params={params} setParams={setParams} />
      <StepSection steps={steps} setSteps={setSteps} params={params} />

      <div className="cua-review-section">
        <div className="cua-review-section-title">Notes from compile time</div>
        <textarea
          className="cua-review-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="cua-review-footer">
        <div className="cua-review-meta">Visibility: <strong>Private</strong></div>
        <div className="cua-review-actions">
          {onDiscard && (
            <button
              type="button"
              className="copilot-modal-btn copilot-modal-btn--danger"
              onClick={() => setConfirmAction("discard")}
            >
              Discard
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              className="copilot-modal-btn copilot-modal-btn--ghost"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
          {onRecompile && (
            <button
              type="button"
              className="copilot-modal-btn copilot-modal-btn--ghost"
              onClick={() => setConfirmAction("recompile")}
            >
              ↻ Re-compile
            </button>
          )}
          <button type="submit" className="copilot-modal-btn copilot-modal-btn--primary">
            {saveLabel}
          </button>
        </div>
      </div>
    </form>

    {confirm && (
      <CopilotModalShell
        title={confirm.title}
        size="sm"
        onClose={() => setConfirmAction(null)}
        footer={
          <>
            <button
              type="button"
              className="copilot-modal-btn copilot-modal-btn--ghost"
              onClick={() => setConfirmAction(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={confirm.confirmClass}
              onClick={() => { setConfirmAction(null); confirm.run(); }}
            >
              {confirm.confirmLabel}
            </button>
          </>
        }
      >
        <p className="copilot-modal-body">{confirm.body}</p>
      </CopilotModalShell>
    )}
    </>
  );
}
